/**
 * Everything the Cards page does that is not rendering.
 *
 * The validation and the blank-to-None normalization stay on the server, in
 * `cards_logic`. This module sends requests and shapes what comes back; it
 * does not decide whether a card is valid.
 */

import { authedFetch } from "./api";
import { monthDay } from "./creditsPage";
import { orThrow } from "./http";
import { formatWhole } from "./money";

export interface CardHeader {
  display_name: string;
  issuer: string;
  annual_fee: number;
  annual_credit_value: number;
  show_credits: boolean;
}

export interface CardEditDefaults {
  name: string;
  issuer: string;
  annual_fee: number;
  anniversary_date: string;
  notes: string;
}

/** Rows are already display-shaped by `cards_logic`; keys vary by kind. */
export type DetailRow = Record<string, unknown>;

/**
 * One credit under a card.
 *
 * `id` does not come from `cards_logic.credit_row` -- that dict carries
 * display values only. The router merges
 * the id in, because without it the browser can render a credit and cannot
 * address it.
 */
export interface CreditRow {
  id: number;
  name: string;
  amount: number;
  cadence_label: string;
  annual_value: number;
  reset_label: string;
  category: string;
  notes_line: string;
  recurring_post_day: number;
  /** 1-12 for a once-a-year post date, 0 when it posts every period. */
  recurring_post_month: number;
}

/** A time-limited perk on the card, ending within 90 days. */
export interface EndingBenefit {
  card: string;
  credit: string;
  end_date: string;
  days_until: number;
}

/** This fee year: what was redeemed, and when the fee next posts. */
export interface FeeYear {
  used: number;
  /** ISO date, or null when the card has no anniversary on file. */
  renews: string | null;
}

export interface CardDto {
  id: number;
  header: CardHeader;
  edit_defaults: CardEditDefaults;
  fee_year: FeeYear;
  /** Soonest first; empty when nothing on the card is ending. Optional because
   *  Hosting can go live minutes before the API that sends it. (2026-09-10) */
  ending?: EndingBenefit[];
  credits: CreditRow[];
  benefits: DetailRow[];
  rates: DetailRow[];
  caps: DetailRow[];
}

/** A selectbox entry: the value the API accepts, and what the live page calls it. */
export interface CadenceOption {
  value: string;
  label: string;
}

export interface CardsDto {
  cards: CardDto[];
  options: {
    cadences: CadenceOption[];
    reset_schedules: CadenceOption[];
  };
}

export interface NewCardInput {
  name: string;
  issuer: string;
  annual_fee: number;
  anniversary_date?: string;
  url_issuer?: string;
  url_review?: string;
  notes?: string;
}

export interface NewCreditInput {
  name: string;
  amount: number;
  cadence: string;
  reset_schedule: string;
  category?: string;
  special_notes?: string;
  recurring_post_day?: number;
}

export async function fetchCards(): Promise<CardsDto> {
  const res = await authedFetch("/api/cards");
  return (await orThrow(res, "Could not load cards")) as CardsDto;
}

export async function createCard(input: NewCardInput): Promise<number> {
  const res = await authedFetch("/api/cards", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body = (await orThrow(res, "Could not add the card")) as { id: number };
  return body.id;
}

export async function updateCard(
  id: number,
  input: Omit<NewCardInput, "url_issuer" | "url_review">,
): Promise<void> {
  // No URL fields, matching the server's narrower edit model. Sending them
  // would be rejected as unknown, but the real point is that an edit form
  // which never showed a URL must not be able to blank one.
  const res = await authedFetch(`/api/cards/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  await orThrow(res, "Could not save the card");
}

/**
 * Delete a card, its credits, and their entire usage history.
 *
 * `confirmName` is required by the server and must equal the card's stored
 * name. It is a parameter rather than something this function derives from the
 * id, deliberately: the point of the confirmation is that a human typed the
 * name of the thing being destroyed, and a helper that looked it up would
 * satisfy the check automatically and remove the guard.
 */
export async function deleteCard(id: number, confirmName: string): Promise<void> {
  const res = await authedFetch(`/api/cards/${id}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ confirm_name: confirmName }),
  });
  await orThrow(res, "Could not delete the card");
}

export async function createCredit(
  cardId: number,
  input: NewCreditInput,
): Promise<number> {
  const res = await authedFetch(`/api/cards/${cardId}/credits`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body = (await orThrow(res, "Could not add the credit")) as { id: number };
  return body.id;
}

export async function updateCredit(
  creditId: number,
  fields: Record<string, unknown>,
): Promise<void> {
  const res = await authedFetch(`/api/credits/${creditId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(fields),
  });
  await orThrow(res, "Could not save the credit");
}

/** A column in one of the card-detail tables. */
export interface Column {
  key: string;
  label: string;
  align?: "right";
  /** How to render the cell. Absent means stringify, which is right for text. */
  format?: (value: unknown) => string;
}

/**
 * A money column.
 *
 * `cap_row` sends `amount` as a bare number, so a $25,000 spending cap
 * rendered as `25000` -- a figure in the same column style as a multiplier,
 * with nothing saying it was dollars. The live view formats it `:,.0f` with a
 * dollar sign. A non-numeric value renders as itself rather than as `$NaN`.
 */
function money(value: unknown): string {
  return typeof value === "number" ? formatWhole(value) : String(value ?? "—");
}

export const BENEFIT_COLUMNS: Column[] = [
  { key: "name", label: "Benefit" },
  { key: "value", label: "Value" },
  { key: "cadence", label: "Cadence" },
  { key: "notes", label: "Notes" },
];

export const RATE_COLUMNS: Column[] = [
  { key: "category", label: "Category" },
  { key: "multiplier", label: "Earns", align: "right" },
  { key: "cap", label: "Cap" },
];

export const CAP_COLUMNS: Column[] = [
  { key: "description", label: "Applies to" },
  { key: "amount", label: "Amount", align: "right", format: money },
  { key: "cadence_label", label: "Cadence" },
  { key: "reset_label", label: "Resets" },
  { key: "notes", label: "Notes" },
];

/** "cadence_label" -> "Cadence label". The fallback, not the normal path. */
export function humanize(key: string): string {
  const words = key.replace(/_/g, " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : key;
}

/**
 * Which columns to render, and what to call them.
 *
 * The page used to head every column with its raw backend key, so a card's
 * benefits table said "cadence_label" and "annual_value" -- field names from
 * `cards_logic`, leaked to a human reader.
 *
 * Named columns alone would have swapped one failure for another: a key the
 * server starts sending would then render nowhere, silently, which is worse
 * than an ugly header. So a key with no entry in the spec still gets a column,
 * under a humanized label. Nothing disappears; known columns just read
 * properly. Order follows the spec, with unknown keys appended in the order
 * the server sent them.
 */
export function columnsFor(rows: DetailRow[], spec: Column[]): Column[] {
  if (rows.length === 0) return [];
  const present = new Set<string>();
  for (const row of rows) for (const key of Object.keys(row)) present.add(key);
  const known = spec.filter((c) => present.has(c.key));
  const claimed = new Set(spec.map((c) => c.key));
  const extra = [...present]
    .filter((k) => !claimed.has(k))
    .map((k) => ({ key: k, label: humanize(k) }));
  return [...known, ...extra];
}

/**
 * The tile's verdict: "Fee covered" once this fee year's redemptions reach the
 * fee, "$X to break even" before that. A card with no fee gets no verdict.
 */
export function breakEven(used: number, fee: number): string | null {
  if (fee <= 0) return null;
  return used >= fee ? "Fee covered" : `${formatWhole(fee - used)} to break even`;
}

/** "renews Mar 15", or null with no anniversary on file. */
export function renewsText(renews: string | null, today?: string): string | null {
  return renews ? `renews ${monthDay(renews, today)}` : null;
}

/**
 * Fee cards first, as tiles with a fee to cover; no-fee cards fold into one
 * row under them, since there is no fee question to answer. Server order is
 * kept within each.
 */
export function splitByFee(cards: CardDto[]): { withFee: CardDto[]; noFee: CardDto[] } {
  return {
    withFee: cards.filter((c) => c.header.annual_fee > 0),
    noFee: cards.filter((c) => c.header.annual_fee <= 0),
  };
}

/** "Saks credit ends Dec 31", the line an ending benefit gets on its tile. */
export function endingText(e: EndingBenefit, today?: string): string {
  return `${e.credit} ends ${monthDay(e.end_date, today)}`;
}

/**
 * The wallet total over the tiles: every card's fee against every card's
 * credits used this fee year, summed in cents so the halves cannot drift.
 */
export function walletTotals(cards: CardDto[]): { annual_fees: number; credits_used: number } {
  let fees = 0;
  let used = 0;
  for (const c of cards) {
    fees += Math.round((c.header.annual_fee || 0) * 100);
    used += Math.round(c.fee_year.used * 100);
  }
  return { annual_fees: fees / 100, credits_used: used / 100 };
}
